import React from 'react';
import {
  Dashboard as DashboardIcon,
  CloudSync as SyncIcon,
  Assignment as LogsIcon,
  Inventory as InventoryIcon,
  Upload as UploadIcon,
  Receipt as ReceiptIcon,
  Inventory2 as Inventory2Icon,
  ListAlt as ListAltIcon,
  AccessTime as AccessTimeIcon,
  DeleteForever as DeleteForeverIcon,
  BuildCircle as BuildCircleIcon,
  LayersClear as LayersClearIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Flag as FlagIcon,
  MiscellaneousServices as MiscellaneousServicesIcon,
  Speed as SpeedIcon,
  ShoppingCart as ShoppingCartIcon,
  Warehouse as WarehouseIcon,
  Autorenew as AutorenewIcon,
  TableChart as TableChartIcon,
  Bookmark as BookmarkIcon,
  Apps as AppsIcon,
  Groups as GroupsIcon,
  PersonSearch as PersonSearchIcon,
  MailOutline as MailOutlineIcon,
  CleaningServices as CleaningServicesIcon,
  Insights as InsightsIcon,
  Policy as PolicyIcon,
  Calculate as CalculateIcon,
  FactCheck as FactCheckIcon,
  DataSaverOn as DataSaverOnIcon,
  ViewInAr as ViewInArIcon,
  RocketLaunch as RocketLaunchIcon,
  BugReport as BugReportIcon,
  Dns as DnsIcon,
  CreditCard as CreditCardIcon,
  Sensors as SensorsIcon,
  AccountTree as AccountTreeIcon,
  Description as DescriptionIcon,
  Usb as UsbIcon,
  Hub as HubIcon,
  FolderZip as FolderZipIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  Forum as ForumIcon,
  NewReleases as NewReleasesIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { ENV } from '@/config/environment';
import { hasSuperAdminAccess } from '@/auth/roleChecks';

export interface PageEntry {
  label: string;
  path: string;
  icon: React.ReactElement;
  keywords?: string[];
  /** Hidden from nav, search and the home grid unless the session holds the superadmin role. */
  superAdminOnly?: boolean;
}

export interface PageGroup {
  label: string;
  icon: React.ReactElement;
  items: PageEntry[];
}

export const pageGroups: PageGroup[] = [
  {
    label: 'Dashboard',
    icon: React.createElement(DashboardIcon),
    items: [
      {
        label: 'Dashboard',
        path: '/',
        icon: React.createElement(DashboardIcon),
        keywords: ['home', 'main'],
      },
    ],
  },
  {
    label: 'Admin',
    icon: React.createElement(AdminPanelSettingsIcon),
    items: [
      {
        label: 'Admin Tenants',
        path: '/admin-tenants',
        icon: React.createElement(AdminPanelSettingsIcon),
        keywords: ['tenant', 'manage', 'admin'],
      },
      {
        label: 'Trial Pipeline',
        path: '/trial-pipeline',
        icon: React.createElement(InsightsIcon),
        keywords: ['trial', 'signup', 'approval', 'queue', 'pipeline', 'prospect', 'convert', 'extend', 'invite'],
      },
      {
        label: 'Contract Requests',
        path: '/contract-requests',
        icon: React.createElement(DescriptionIcon),
        keywords: ['contract', 'enrollment', 'billing', 'autopay', 'stripe', 'phases', 'schedule'],
      },
      {
        label: 'Trial Policy',
        path: '/trial-policy',
        icon: React.createElement(PolicyIcon),
        keywords: ['trial', 'policy', 'limits', 'caps', 'flags', 'standard', 'migrator', 'bounds'],
      },
      {
        label: 'Feature Flags',
        path: '/feature-flags',
        superAdminOnly: true,
        icon: React.createElement(FlagIcon),
        keywords: ['feature', 'flag', 'toggle', 'config'],
      },
      {
        label: 'Email Delivery',
        path: '/email-delivery',
        icon: React.createElement(MailOutlineIcon),
        keywords: ['email', 'delivery', 'confirmation', 'acknowledgment', 'invoice', 'kill switch', 'sending', 'blocked'],
      },
      {
        label: 'Background Services',
        path: '/background-services',
        icon: React.createElement(MiscellaneousServicesIcon),
        keywords: ['background', 'service', 'config', 'timer', 'pod'],
      },
      {
        label: 'Payment Processors',
        path: '/payment-processors',
        icon: React.createElement(CreditCardIcon),
        keywords: ['payment', 'processor', 'stripe', 'billing', 'cap', 'online payment'],
      },
      {
        label: 'Parent Companies',
        path: '/parent-companies',
        icon: React.createElement(AccountTreeIcon),
        keywords: ['parent company', 'tenants', 'group', 'administrators', 'cross-tenant'],
      },
      {
        label: 'Fernie Conversations',
        path: '/fernie-conversations',
        icon: React.createElement(ForumIcon),
        keywords: ['fernie', 'ai', 'chat', 'conversations', 'transcript', 'assistant', 'thumbs down', 'logs'],
        superAdminOnly: true,
      },
      {
        label: "What's new",
        path: '/whats-new',
        icon: React.createElement(NewReleasesIcon),
        keywords: ['whats new', "what's new", 'release', 'features', 'announcements', 'tenant preview', 'try early', 'coming soon'],
        superAdminOnly: true,
      },
      {
        label: 'Test Logins',
        path: '/test-logins',
        icon: React.createElement(VpnKeyIcon),
        keywords: ['test login', 'test logins', 'sessions', 'credentials', 'username', 'roles', 'purpose', 'fusionauth'],
        superAdminOnly: true,
      },
      {
        label: 'Deploy Dashboard',
        path: '/deploy-dashboard',
        icon: React.createElement(RocketLaunchIcon),
        keywords: ['deploy', 'dashboard', 'release', 'approvals', 'aks', 'namespace', 'health', 'pipeline', 'rollout'],
      },
      {
        label: 'QA Dashboard',
        path: '/qa-dashboard',
        icon: React.createElement(BugReportIcon),
        keywords: ['qa', 'test', 'tests', 'dashboard', 'playwright', 'automation', 'health', 'failures', 'regression', 'stage'],
      },
      {
        label: 'ACR Image Tags',
        path: '/acr',
        icon: React.createElement(ViewInArIcon),
        keywords: ['acr', 'image', 'tag', 'tags', 'container', 'registry', 'silverfern', 'cleanup', 'repository', 'repos'],
      },
      {
        label: 'Portal Custom Domains',
        path: '/portal-custom-domains',
        icon: React.createElement(DnsIcon),
        keywords: ['portal', 'domain', 'byo', 'ingress', 'custom', 'tls', 'reactor'],
      },
      {
        label: 'Reset Tenant',
        path: '/reset-tenant',
        icon: React.createElement(DeleteForeverIcon),
        keywords: ['reset', 'wipe', 'tenant', 'streams', 'delete', 'remove', 'blank', 'clear'],
      },
      {
        label: 'Data Corrections',
        path: '/data-corrections',
        icon: React.createElement(BuildCircleIcon),
        keywords: ['data', 'correction', 'corrections', 'fix', 'restate', 'converge', 'backfill', 'approval', 'run'],
        superAdminOnly: true,
      },
      {
        label: 'Delete Streams',
        path: '/delete-streams',
        icon: React.createElement(LayersClearIcon),
        keywords: ['delete', 'streams', 'prefix', 'event'],
      },
      {
        label: 'FA Applications',
        path: '/role-mgmt/applications',
        icon: React.createElement(AppsIcon),
        keywords: ['fusionauth', 'fa', 'application', 'role', 'roles', 'role management'],
      },
      {
        label: 'FA Groups',
        path: '/role-mgmt/groups',
        icon: React.createElement(GroupsIcon),
        keywords: ['fusionauth', 'fa', 'group', 'groups', 'member', 'members', 'role', 'role management'],
      },
      {
        label: 'FA Users',
        path: '/role-mgmt/users',
        icon: React.createElement(PersonSearchIcon),
        keywords: ['fusionauth', 'fa', 'user', 'users', 'search', 'role management'],
      },
      {
        label: 'Invites',
        path: '/role-mgmt/invites',
        icon: React.createElement(MailOutlineIcon),
        keywords: ['invite', 'invites', 'invitation', 'onboarding', 'register', 'role management'],
      },
    ],
  },
  {
    label: 'IoT',
    icon: React.createElement(HubIcon),
    items: [
      {
        label: 'IoT Fleet',
        path: '/iot',
        icon: React.createElement(SensorsIcon),
        keywords: ['iot', 'devices', 'ingestion', 'fleet', 'telemetry'],
      },
      {
        label: 'Device Provisioning',
        path: '/devices/provision',
        icon: React.createElement(PrecisionManufacturingIcon),
        keywords: [
          'provision',
          'provisioning',
          'wizard',
          'factory',
          'edgebox',
          'serial',
          'claim',
          'identity',
          'device',
          'iot',
          'register',
          'claim code',
          'code',
          'sensor',
          'beam break',
        ],
      },
      {
        label: 'Firmware Flasher',
        path: '/devices/flash',
        icon: React.createElement(UsbIcon),
        keywords: ['firmware', 'flash', 'flasher', 'edgebox', 'device', 'usb', 'web serial', 'esp32', 'chip'],
      },
      {
        label: 'Firmware Files',
        path: '/firmware-files',
        icon: React.createElement(FolderZipIcon),
        keywords: ['firmware', 'files', 'blob', 'container', 'release', 'version', 'download', 'edgebox', 'publish'],
      },
    ],
  },
  {
    label: 'Restock Admin',
    icon: React.createElement(AutorenewIcon),
    items: [
      {
        label: 'Daily Transaction Summaries',
        path: '/restock-admin/daily-transaction-summaries',
        icon: React.createElement(TableChartIcon),
        keywords: [
          'restock',
          'daily',
          'transaction',
          'summary',
          'sales',
          'import',
          'bpf',
        ],
      },
      {
        label: 'Restock Calc Results',
        path: '/restock-admin/restock-calc-results',
        icon: React.createElement(CalculateIcon),
        keywords: [
          'restock',
          'calc',
          'calculation',
          'result',
          'demand',
          'replenishment',
          'inspect',
          'lookup',
        ],
      },
      {
        label: 'Data Validity',
        path: '/restock-admin/data-validity',
        icon: React.createElement(FactCheckIcon),
        keywords: [
          'restock',
          'data',
          'validity',
          'health',
          'checks',
          'rag',
          'freshness',
          'demand',
        ],
      },
      // Data Seeding is a non-prod tool; hidden from nav in production.
      ...(!ENV.isProd
        ? [
            {
              label: 'Data Seeding',
              path: '/restock-admin/data-seeding',
              icon: React.createElement(DataSaverOnIcon),
              keywords: [
                'restock',
                'seed',
                'seeding',
                'data',
                'import',
                'demand',
                'achievable',
                'pipeline',
                'bpf',
              ],
            },
          ]
        : []),
    ],
  },
  {
    label: 'System',
    icon: React.createElement(SpeedIcon),
    items: [
      {
        label: 'WSAPI Projection Status',
        path: '/projection-status',
        icon: React.createElement(SpeedIcon),
        keywords: ['projection', 'status', 'wsapi'],
      },
      {
        label: 'WSAPI Entity Checkpoints',
        path: '/entity-checkpoints',
        icon: React.createElement(BookmarkIcon),
        keywords: ['entity', 'checkpoint', 'wsapi', 'position', 'system'],
      },
      {
        label: 'Scavenge',
        path: '/scavenge',
        icon: React.createElement(CleaningServicesIcon),
        keywords: ['scavenge', 'kurrentdb', 'cleanup', 'eventstore', 'config'],
      },
      {
        label: 'Event Stream Aggregate',
        path: '/event-stream-aggregate',
        icon: React.createElement(InsightsIcon),
        keywords: ['event', 'stream', 'aggregate', 'snapshot', 'chart', 'transparency'],
      },
      {
        label: 'Time Logger',
        path: '/time-logger',
        icon: React.createElement(AccessTimeIcon),
        keywords: ['time', 'logger', 'tracking'],
      },
    ],
  },
  {
    label: 'Orders',
    icon: React.createElement(ShoppingCartIcon),
    items: [
      {
        label: 'Order Lookup',
        path: '/order-lookup-v2',
        icon: React.createElement(ReceiptIcon),
        keywords: ['order', 'lookup', 'search'],
      },
      {
        label: 'Order Line Items',
        path: '/order-line-items',
        icon: React.createElement(ListAltIcon),
        keywords: ['order', 'line', 'items'],
      },
      {
        label: 'CSV Import',
        path: '/csv-import',
        icon: React.createElement(UploadIcon),
        keywords: ['csv', 'import', 'upload', 'orders'],
      },
    ],
  },
  {
    label: 'Inventory',
    icon: React.createElement(WarehouseIcon),
    items: [
      {
        label: 'Inventory',
        path: '/inventory',
        icon: React.createElement(Inventory2Icon),
        keywords: ['inventory', 'stock'],
      },
      {
        label: 'On Hand Process',
        path: '/on-hand-process',
        icon: React.createElement(InventoryIcon),
        keywords: ['on hand', 'process', 'inventory', 'adjustment'],
      },
    ],
  },
  {
    label: 'Data Sync',
    icon: React.createElement(SyncIcon),
    items: [
      {
        label: 'SFTP Sync',
        path: '/sftp-sync',
        icon: React.createElement(SyncIcon),
        keywords: ['sftp', 'sync', 'config', 'configuration'],
      },
      {
        label: 'Iteration Logs',
        path: '/sftp-sync/logs',
        icon: React.createElement(LogsIcon),
        keywords: ['iteration', 'logs', 'sftp', 'sync'],
      },
    ],
  },
];

const isVisible = (page: PageEntry): boolean => !page.superAdminOnly || hasSuperAdminAccess();

/** Groups with the pages the current session may not open removed; empty groups are dropped. */
export const getVisiblePageGroups = (): PageGroup[] =>
  pageGroups
    .map((group) => ({ ...group, items: group.items.filter(isVisible) }))
    .filter((group) => group.items.length > 0);

/** Flat list of all pages for search */
export const allPages: PageEntry[] = pageGroups.flatMap((group) => group.items);

/** Pages the current session may open. */
export const getVisiblePages = (): PageEntry[] => allPages.filter(isVisible);

export const pageMatchesQuery = (page: PageEntry, query: string): boolean => {
  const needle = query.toLowerCase();
  if (page.label.toLowerCase().includes(needle)) return true;
  return page.keywords?.some((keyword) => keyword.toLowerCase().includes(needle)) ?? false;
};
